// Stato del builder: funzioni pure, nessun DOM. Ogni azione ritorna un
// NUOVO stato; se un guardrail la vieta ritorna lo stato di partenza.

import {
  MAX_ELEMENTS,
  CUSTOM_TEXT_MAX_LENGTH,
  CUSTOM_ELEMENT_KEY,
  normalizeCellRun,
  defaultElementOptions,
  elementFigureLevers,
  findByKey,
  paperForTechnique,
  findToTone,
  BUILDER_PALETTES,
} from './builderCatalog';

export function createBuilderState({ orientation = 'vertical' } = {}) {
  return {
    orientation,
    // Lo sfondo nasce per ricevere foto e testi. Il profilo parte acceso sui
    // nuovi temi, ma il compositore lo applica solo al valore booleano `true`:
    // uno stato legacy senza questo campo conserva quindi la resa precedente.
    textReadability: true,
    base: {
      type: 'plain',
      direction: 'left',
      spread: 'narrow',
      gradientIntensity: 'medium',
      // Colori espliciti della sfumatura: {key} preset, {hex} libero, null =
      // la partenza eredita la palette del passo Stile finché non si sceglie.
      fromColor: null,
      toColor: { key: 'white' },
      mirror: false,
      sceneKey: 'sky',
      scenePlacement: 'full',
      sceneExtent: 'third',
      sceneFade: true,
      sacredFigure: 'holymary',
      sacredFraming: 'halfbust',
      sacredPerspective: 'frontal',
      sacredMood: 'serene',
      // Testi liberi delle voci «Personalizzata» delle nove dimensioni (spec
      // 2026-08-07-builder-custom-values-design.md): come customStyle/
      // customCoverage della cornice, vivono accanto alle rispettive
      // selezioni e restano vuoti finché quella selezione non è 'custom'.
      customScene: '',
      customSacredFigure: '',
      customSacredFraming: '',
      customSacredPerspective: '',
      customSacredMood: '',
      paper: 'cotton',
      paperOn: false,
      grain: 'soft',
      paperTouched: false,
      material: 'marble',
      materialIntensity: 'medium',
    },
    style: {
      technique: 'minimal',
      intensity: 'natural',
      palette: 'none',
      atmosphere: 'neutral',
      customTechnique: '',
      customIntensity: '',
      customAtmosphere: '',
      customPalette: '',
    },
    elements: [],
    selection: null,
    frame: {
      style: 'none',
      richness: 'sober',
      coverage: 'full',
      relation: 'overlay',
      customStyle: '',
      customCoverage: '',
      margins: { top: 'none', bottom: 'none', left: 'none', right: 'none' },
    },
    details: '',
  };
}

const cloneState = (state) => ({
  ...state,
  base: { ...state.base },
  style: { ...state.style },
  frame: { ...state.frame },
  elements: state.elements.map((element) => ({
    ...element,
    cells: [...element.cells],
    ...(element.options ? { options: { ...element.options } } : {}),
  })),
  selection: state.selection ? { cells: [...state.selection.cells] } : null,
});

const withBase = (state, patch) => {
  const next = cloneState(state);
  next.base = { ...next.base, ...patch };
  return next;
};

const withStyle = (state, patch) => {
  const next = cloneState(state);
  next.style = { ...next.style, ...patch };
  return next;
};

const withFrame = (state, patch) => {
  const next = cloneState(state);
  next.frame = { ...next.frame, ...patch };
  return next;
};

export const setEssence = (state, type) => withBase(state, { type });
export const setDirection = (state, direction) =>
  withBase(state, { direction });
export const setScene = (state, sceneKey) => withBase(state, { sceneKey });
export const setSpread = (state, spread) => withBase(state, { spread });
export const setGradientIntensity = (state, gradientIntensity) =>
  withBase(state, { gradientIntensity });

const isValidColorRef = (ref) => {
  if (!ref || typeof ref !== 'object') {
    return false;
  }
  if (typeof ref.hex === 'string') {
    return /^#[0-9a-f]{6}$/i.test(ref.hex);
  }
  return Boolean(ref.key && findToTone(ref.key));
};

export const setFromColor = (state, ref) =>
  isValidColorRef(ref) ? withBase(state, { fromColor: ref }) : state;
export const setToColor = (state, ref) =>
  isValidColorRef(ref) ? withBase(state, { toColor: ref }) : state;

// Il fondo grigino di sempre: palette none eredita questa partenza, che ai
// fini del prompt vale «bianco» ma nel CSS resta il velo #e9e9e9 attuale.
const NEUTRAL_FROM = {
  key: 'none',
  tint: '#e9e9e9',
  toneName: 'bianco',
  isWhite: true,
};

const colorRefInfo = (ref) => {
  if (ref.hex) {
    const hex = ref.hex.toLowerCase();
    return { hex, tint: hex, toneName: null, isWhite: hex === '#ffffff' };
  }
  const tone = findToTone(ref.key);
  return {
    key: tone.key,
    tint: tone.tint,
    toneName: tone.toneName,
    isWhite: tone.key === 'white',
  };
};

export const resolvedGradientEndpoints = (state) => {
  let from;
  if (state.base.fromColor) {
    from = colorRefInfo(state.base.fromColor);
  } else {
    const palette = findByKey(BUILDER_PALETTES, state.style.palette);
    from =
      palette && palette.key !== 'none'
        ? colorRefInfo({ key: palette.key })
        : NEUTRAL_FROM;
  }
  const to = colorRefInfo(state.base.toColor || { key: 'white' });
  return { from, to };
};

export const setMirror = (state, mirror) => withBase(state, { mirror });
export const setScenePlacement = (state, scenePlacement) =>
  withBase(state, { scenePlacement });
export const setSceneExtent = (state, sceneExtent) =>
  withBase(state, { sceneExtent });
export const setSceneFade = (state, sceneFade) =>
  withBase(state, { sceneFade });
export const setSacredFigure = (state, sacredFigure) =>
  withBase(state, { sacredFigure });
export const setSacredFraming = (state, sacredFraming) =>
  withBase(state, { sacredFraming });
export const setSacredPerspective = (state, sacredPerspective) =>
  withBase(state, { sacredPerspective });
export const setSacredMood = (state, sacredMood) =>
  withBase(state, { sacredMood });

// Testo libero delle nove dimensioni «Personalizzata»: troncato a
// CUSTOM_TEXT_MAX_LENGTH come il customText degli elementi (buildElementPayload
// più sopra); un input non stringa (null/undefined, o un tipo inatteso) ricade
// sulla stringa vuota invece di propagare un valore che romperebbe il prompt.
const truncatedCustomText = (text) =>
  (typeof text === 'string' ? text : '').slice(0, CUSTOM_TEXT_MAX_LENGTH);

export const setCustomScene = (state, customScene) =>
  withBase(state, { customScene: truncatedCustomText(customScene) });
export const setCustomSacredFigure = (state, customSacredFigure) =>
  withBase(state, {
    customSacredFigure: truncatedCustomText(customSacredFigure),
  });
export const setCustomSacredFraming = (state, customSacredFraming) =>
  withBase(state, {
    customSacredFraming: truncatedCustomText(customSacredFraming),
  });
export const setCustomSacredPerspective = (state, customSacredPerspective) =>
  withBase(state, {
    customSacredPerspective: truncatedCustomText(customSacredPerspective),
  });
export const setCustomSacredMood = (state, customSacredMood) =>
  withBase(state, { customSacredMood: truncatedCustomText(customSacredMood) });

// `paperTouched` distingue «la carta gliel’ha suggerita la tecnica» da «la
// carta l’ha scelta l’utente»: dal secondo caso in poi la tecnica non la
// sposta più (vedi setTechnique).
export const setPaper = (state, paper) =>
  withBase(state, { paper, paperTouched: true });
export const setPaperOn = (state, paperOn) => withBase(state, { paperOn });
export const setGrain = (state, grain) => withBase(state, { grain });
export const setMaterial = (state, material) => withBase(state, { material });
export const setMaterialIntensity = (state, materialIntensity) =>
  withBase(state, { materialIntensity });

// La carta è attiva quando è lei il fondo, oppure quando è stata accesa sotto
// uno sfumato o una scena. Su «plain» non esiste un checkbox per spegnerla:
// se paperOn resta `true` da un giro su sfumato/scena (setEssence non lo
// tocca) o arriva così da un brief vecchio, qui lo ignoriamo comunque, così
// il bianco liscio resta sempre liscio. Vive in questo modulo, non in
// structuredPrompt.js, perché anche l’anteprima del blueprint deve leggere
// esattamente questa stessa regola, non una copia che può divergere.
export const isPaperActive = (state) =>
  state.base.type === 'paper' ||
  (Boolean(state.base.paperOn) &&
    (state.base.type === 'gradient' || state.base.type === 'scene'));

// Tecnica, intensità dei colori e atmosfera sono controlli artistici: la UI
// li mostra solo quando esiste qualcosa da rendere. Il prompt usa lo stesso
// predicato, così valori nascosti o residui non alterano un fondo astratto.
export const hasImagery = (state) =>
  state.base.type === 'scene' || state.elements.length > 0;

// Una materia nuda non ha una tecnica pittorica né un’intensità dei colori,
// ma la sua resa ottica cambia davvero con la luce (soprattutto alabastro,
// madreperla, onice e vetro satinato). L’atmosfera resta quindi disponibile
// sui materiali senza fingere che siano una scena.
export const supportsAtmosphere = (state) =>
  hasImagery(state) || state.base.type === 'material';

// La tecnica suggerisce il supporto naturale, ma non lo accende: su sfumato e
// scena la carta resta spenta finché non la si attiva, altrimenti toccare la
// tecnica farebbe comparire una texture che nessuno ha chiesto.
export const setTechnique = (state, technique) => {
  const next = withStyle(state, { technique });
  if (!next.base.paperTouched) {
    next.base = { ...next.base, paper: paperForTechnique(technique) };
  }
  return next;
};
export const setIntensity = (state, intensity) =>
  withStyle(state, { intensity });
export const setPalette = (state, palette) => withStyle(state, { palette });
export const setAtmosphere = (state, atmosphere) =>
  withStyle(state, { atmosphere });

export const setCustomTechnique = (state, customTechnique) =>
  withStyle(state, { customTechnique: truncatedCustomText(customTechnique) });
export const setCustomIntensity = (state, customIntensity) =>
  withStyle(state, { customIntensity: truncatedCustomText(customIntensity) });
export const setCustomAtmosphere = (state, customAtmosphere) =>
  withStyle(state, {
    customAtmosphere: truncatedCustomText(customAtmosphere),
  });
export const setCustomPalette = (state, customPalette) =>
  withStyle(state, { customPalette: truncatedCustomText(customPalette) });

export const setTextReadability = (state, textReadability) => {
  const next = cloneState(state);
  next.textReadability = Boolean(textReadability);
  return next;
};

export const setFrameStyle = (state, style) => withFrame(state, { style });
export const setFrameRichness = (state, richness) =>
  withFrame(state, { richness });
export const setFrameCoverage = (state, coverage) =>
  withFrame(state, { coverage });
export const setFrameRelation = (state, relation) =>
  withFrame(state, { relation });
export const setFrameCustomStyle = (state, customStyle) =>
  withFrame(state, { customStyle });
export const setFrameCustomCoverage = (state, customCoverage) =>
  withFrame(state, { customCoverage });
export const setFrameMargins = (state, level) =>
  withFrame(state, {
    margins: { top: level, bottom: level, left: level, right: level },
  });
export const setFrameMargin = (state, side, level) =>
  withFrame(state, { margins: { ...state.frame.margins, [side]: level } });

export function setDetails(state, details) {
  const next = cloneState(state);
  next.details = details;
  return next;
}

export const getElementByCell = (state, cell) =>
  state.elements.find((element) => element.cells.includes(cell)) || null;

export const canAddElement = (state) => state.elements.length < MAX_ELEMENTS;

const isCellFree = (state, cell) => !getElementByCell(state, cell);

export function startCellSelection(state, cell) {
  if (!isCellFree(state, cell) || !canAddElement(state)) {
    return state;
  }
  if (!normalizeCellRun([cell])) {
    return state;
  }
  const next = cloneState(state);
  next.selection = { cells: [cell] };
  return next;
}

export function clearSelection(state) {
  if (!state.selection) {
    return state;
  }
  const next = cloneState(state);
  next.selection = null;
  return next;
}

export function toggleSelectionCell(state, cell) {
  if (!state.selection) {
    return startCellSelection(state, cell);
  }
  const current = state.selection.cells;
  let candidate;
  if (current.includes(cell)) {
    candidate = current.filter((entry) => entry !== cell);
  } else {
    if (!isCellFree(state, cell)) {
      return state;
    }
    candidate = [...current, cell];
  }
  if (!candidate.length) {
    return clearSelection(state);
  }
  const normalized = normalizeCellRun(candidate);
  if (!normalized) {
    return state;
  }
  const next = cloneState(state);
  next.selection = { cells: normalized };
  return next;
}

const buildElementPayload = ({ itemKey, customText = '' }) => {
  const trimmed = (customText ?? '').trim().slice(0, CUSTOM_TEXT_MAX_LENGTH);
  if (itemKey === CUSTOM_ELEMENT_KEY && !trimmed) {
    return null;
  }
  const element = { itemKey };
  if (itemKey === CUSTOM_ELEMENT_KEY) {
    element.customText = trimmed;
  }
  const options = defaultElementOptions(itemKey);
  if (options) {
    element.options = options;
  }
  return element;
};

export function confirmSelectionElement(state, { itemKey, customText } = {}) {
  if (!state.selection || !canAddElement(state)) {
    return state;
  }
  const sanitized = buildElementPayload({ itemKey, customText });
  if (!sanitized) {
    return state;
  }
  const next = cloneState(state);
  next.elements.push({ cells: [...state.selection.cells], ...sanitized });
  next.selection = null;
  return next;
}

export function setElementItem(state, cell, { itemKey, customText } = {}) {
  const element = getElementByCell(state, cell);
  if (!element) {
    return state;
  }
  const sanitized = buildElementPayload({ itemKey, customText });
  if (!sanitized) {
    return state;
  }
  const next = cloneState(state);
  const index = next.elements.findIndex((entry) => entry.cells.includes(cell));
  next.elements[index] = { cells: [...element.cells], ...sanitized };
  return next;
}

export function setElementOption(state, cell, leverKey, valueKey) {
  const element = getElementByCell(state, cell);
  if (!element || !element.options) {
    return state;
  }
  const levers = elementFigureLevers(element.itemKey) || [];
  const lever = levers.find((entry) => entry.key === leverKey);
  if (!lever || !findByKey(lever.options, valueKey)) {
    return state;
  }
  const next = cloneState(state);
  const index = next.elements.findIndex((entry) => entry.cells.includes(cell));
  next.elements[index] = {
    ...next.elements[index],
    options: { ...next.elements[index].options, [leverKey]: valueKey },
  };
  return next;
}

export function toggleElementCell(state, elementCell, cell) {
  const element = getElementByCell(state, elementCell);
  if (!element) {
    return state;
  }
  let candidate;
  if (element.cells.includes(cell)) {
    candidate = element.cells.filter((entry) => entry !== cell);
    if (!candidate.length) {
      return state;
    }
  } else {
    const owner = getElementByCell(state, cell);
    if (owner && owner !== element) {
      return state;
    }
    candidate = [...element.cells, cell];
  }
  const normalized = normalizeCellRun(candidate);
  if (!normalized) {
    return state;
  }
  const next = cloneState(state);
  const index = next.elements.findIndex((entry) =>
    entry.cells.includes(elementCell)
  );
  next.elements[index] = { ...next.elements[index], cells: normalized };
  return next;
}

export function removeElementByCell(state, cell) {
  const element = getElementByCell(state, cell);
  if (!element) {
    return state;
  }
  const next = cloneState(state);
  next.elements = next.elements.filter((entry) => !entry.cells.includes(cell));
  return next;
}
